import React, { FC, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { Collections, Edit as EditIcon, Image as ImageIcon, MoreVert, Warning } from '@mui/icons-material';
import styled from '@emotion/styled';
import { v4 as uuid } from 'uuid';
import { useGuideCmsContext } from '../../GuideCmsContext';
import { useConfig } from '../../../../env';
import { SidebarHeading } from '../SidebarHeading';
import {
  getFileNames,
  getImageFiguresWithInstances,
  getMissingFileWarning,
  getThumbnailUrlFromImageFigure,
  ImageFigureWithInstances,
  mapToAttributesFromImageFigure,
  FigureInstance,
} from '../../figures/figureHelper';
import { getFullDescription, getHtmlId, useNumberedNodes } from '../../tiptap/numbering';
import { scrollElementIntoViewById } from '../../scrolling';
import { useModal } from '../../../../dialogs/useModal';
import { GuideFigureDialog, GuideFigureDialogProps, GuideFigureDialogResult } from '../../figures/GuideFigureDialog';
import { theme } from '../../../../theme';

const StyledContainer = styled.div`
  padding: ${theme.spacing(2)};
  height: 100%;
  padding-bottom: 100px;
  overflow-y: scroll;
`;

const StyledGrid = styled.div`
  display: grid;
  margin-top: ${theme.spacing(2)};
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing(2)};
`;

const StyledCardPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  background-color: #f5f5f5;
`;

export const FiguresSidebar: FC = () => {
  const { editor, guideId } = useGuideCmsContext();
  const config = useConfig();
  const [figures, setFigures] = useState<ImageFigureWithInstances[]>([]);
  const numberedNodes = useNumberedNodes();
  const modal = useModal<GuideFigureDialogProps, GuideFigureDialogResult>({ data: { isNew: false } });

  const [canInsert, setCanInsert] = useState(false);

  useEffect(() => {
    if (!editor) return;
    const updateFigures = () => {
      setFigures(getImageFiguresWithInstances(editor));
    };
    const updateCanInsert = () => {
      setCanInsert(editor.isEditable && editor.can().chain().insertImageFigure().run());
    };
    updateFigures();
    updateCanInsert();
    editor.on('update', updateFigures);
    editor.on('selectionUpdate', updateCanInsert);
    editor.on('update', updateCanInsert);
    return () => {
      editor.off('update', updateFigures);
      editor.off('selectionUpdate', updateCanInsert);
      editor.off('update', updateCanInsert);
    };
  }, [editor]);

  const handleInsertCopy = (figure: ImageFigureWithInstances) => {
    if (!editor) return;
    editor.chain().focus().insertImageFigure(mapToAttributesFromImageFigure(uuid(), figure)).run();
  };

  const handleEdit = (figureId: string) => {
    modal.open({ editFigureId: figureId, isNew: false });
  };

  const handleInstanceClick = (instance: FigureInstance) => {
    const numberedNode = numberedNodes.get(instance.nodeId);
    if (!numberedNode) return;
    scrollElementIntoViewById(getHtmlId(numberedNode));
  };

  if (!editor) return null;

  return (
    <>
      {modal.isOpen && <GuideFigureDialog {...modal} />}
      <StyledContainer>
        <SidebarHeading icon={<ImageIcon />}>Figurer i anvisningen</SidebarHeading>
        <StyledGrid>
          {figures.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Ingen figurer i anvisningen.
            </Typography>
          )}
          {figures.map((figure) => {
            const thumbnailUrl = getThumbnailUrlFromImageFigure(config, guideId, figure);
            const fileNames = getFileNames(figure);
            const fileWarning = getMissingFileWarning(figure);
            return (
              <Card key={figure.figureId} variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ position: 'relative' }}>
                  {thumbnailUrl ? (
                    <CardMedia sx={{ height: 100 }} image={thumbnailUrl} />
                  ) : (
                    <StyledCardPlaceholder>
                      <ImageIcon color="disabled" sx={{ fontSize: 36 }} />
                    </StyledCardPlaceholder>
                  )}
                  {fileWarning && (
                    <Chip label={fileWarning} size="small" color="warning" icon={<Warning />} sx={{ position: 'absolute', top: 6, left: 6 }} />
                  )}
                </div>
                <CardContent sx={{ padding: theme.spacing(1, 1), '&:last-child': { pb: 1 }, flexGrow: 1 }}>
                  {figure.instances.length > 0 && (
                    <Stack direction="row" gap={0.5} flexWrap="wrap" mb={0.5}>
                      {figure.instances.map((instance) => {
                        const numberedNode = numberedNodes.get(instance.nodeId);
                        const label = numberedNode ? getFullDescription(numberedNode) : `Fig. ${instance.numbering}`;
                        return (
                          <Chip
                            key={instance.nodeId}
                            label={label}
                            size="small"
                            variant="outlined"
                            onClick={() => handleInstanceClick(instance)}
                            title="Gå til figur"
                          />
                        );
                      })}
                    </Stack>
                  )}
                  {fileNames.small && (
                    <Typography variant="body2" color="textSecondary" noWrap title={fileNames.small}>
                      Liten: {fileNames.small}
                    </Typography>
                  )}
                  {fileNames.large && (
                    <Typography variant="body2" color="textSecondary" noWrap title={fileNames.large}>
                      Stor: {fileNames.large}
                    </Typography>
                  )}
                  {fileNames.original && (
                    <Typography variant="body2" color="textSecondary" noWrap title={fileNames.original}>
                      DWG: {fileNames.original}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <Stack direction="row" justifyContent="space-between" width="100%">
                    <Button size="small" onClick={() => handleEdit(figure.figureId)} startIcon={<EditIcon />}>
                      Rediger
                    </Button>
                    <FigureMoreMenu disabled={!canInsert} onInsertAgain={() => handleInsertCopy(figure)} />
                  </Stack>
                </CardActions>
              </Card>
            );
          })}
        </StyledGrid>
      </StyledContainer>
    </>
  );
};

const FigureMoreMenu: FC<{ disabled: boolean; onInsertAgain: () => void }> = ({ disabled, onInsertAgain }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} title="Flere valg">
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)} disableScrollLock>
        <MenuItem
          disabled={disabled}
          onClick={() => {
            onInsertAgain();
            setAnchorEl(null);
          }}>
          <ListItemIcon>
            <Collections fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sett inn igjen</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
